import React from 'react';
import { Plus, Edit2, Trash2, Phone, Mail, ShoppingCart, Users } from 'lucide-react';

const Staff = ({
  staff: staffProp = [],
  loading = false,
  orders = [],
  onAddStaff,
  onEditStaff,
  onDeleteStaff,
  onToggleStatus,
}) => {
  const staff = Array.isArray(staffProp) ? staffProp : [];
  const pendingOrders = orders.filter((o) => o.status === 'pending').length;

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-800 tracking-tight">
              Staff Directory
            </h1>
            <p className="text-slate-500 mt-1.5 font-medium">Manage and audit your active team members and access roles.</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="bg-orange-50/60 border border-orange-100/60 px-5 py-2.5 rounded-2xl shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm shadow-orange-500/10">
                  <ShoppingCart size={14} />
                </div>
                <div>
                  <span className="text-[10px] font-bold text-orange-650 uppercase tracking-wider block">Pending Orders</span>
                  <span className="text-orange-800 font-extrabold text-sm">{pendingOrders}</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onAddStaff?.()}
              className="ui-btn-primary"
            >
              <Plus size={18} />
              <span>Add Staff</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {staff.map((member) => (
          <div
            key={member.id}
            className="ui-card ui-card-pad ui-card-hover flex flex-col justify-between"
          >
            <div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-extrabold text-slate-800 text-base tracking-tight">{member.name}</h3>
                  <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mt-1">{member.designation || member.position || 'Staff Member'}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {onToggleStatus && (
                    <button
                      onClick={() => onToggleStatus(member.id)}
                      className={`px-2.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider cursor-pointer border ${
                        (member.status || '').toString().toLowerCase() === 'active'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100/60 hover:bg-emerald-100'
                          : 'bg-rose-50 text-rose-700 border-rose-100/60 hover:bg-rose-100'
                      }`}
                      title="Click to toggle status"
                    >
                      {member.status || 'inactive'}
                    </button>
                  )}
                  {onEditStaff && (
                    <button
                      type="button"
                      onClick={() => onEditStaff(member)}
                      className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-100/30 rounded-lg transition-all cursor-pointer shadow-sm"
                      title="Edit Staff Member"
                    >
                      <Edit2 size={13} />
                    </button>
                  )}
                  {onDeleteStaff && (
                    <button
                      type="button"
                      onClick={() => window.confirm('Remove this staff member?') && onDeleteStaff(member.id)}
                      className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-100/30 rounded-lg transition-all cursor-pointer shadow-sm"
                      title="Delete Staff Member"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-3 pt-3 border-t border-slate-100/80">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-50 border border-slate-200/50 text-slate-500">
                    <Phone size={13} />
                  </div>
                  <span className="text-xs text-slate-600 font-semibold">{member.phone || '—'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-50 border border-slate-200/50 text-slate-500">
                    <Mail size={13} />
                  </div>
                  <span className="text-xs text-slate-600 font-semibold truncate" title={member.email}>{member.email || '—'}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!loading && staff.length === 0 && (
        <div className="ui-card p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-slate-50 border border-slate-200/60 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner shrink-0">
            <Users size={28} className="text-slate-400" />
          </div>
          <p className="text-slate-500 font-semibold text-sm">No staff members logged yet. Add one to activate their profile.</p>
        </div>
      )}
    </div>
  );
};

export default Staff;
